import { z } from "zod";
import { isoDateTimeSchema } from "./dates";
import { evaluateDocumentValidity } from "./document-validity";
import {
  DRIVER_DOCUMENT_FILES,
  findDriverDocument,
  findDriverDocumentFile,
} from "./driver-documents";

/**
 * A driver's document situation, derived from their documents and today's date.
 * Informational: it tags drivers for admins and nudges drivers; it never blocks work.
 */

/** Most severe first; the driver's status is the most severe issue. */
export const DRIVER_DOCUMENT_ISSUE_KINDS = [
  "EXPIRED",
  "MISSING",
  "REJECTED",
  "EXPIRING",
  "PENDING_REVIEW",
  "DATE_UNVERIFIED",
] as const;
export type DriverDocumentIssueKind = (typeof DRIVER_DOCUMENT_ISSUE_KINDS)[number];

export const DRIVER_DOCUMENT_COMPLIANCE = [...DRIVER_DOCUMENT_ISSUE_KINDS, "COMPLETE"] as const;
export type DriverDocumentCompliance = (typeof DRIVER_DOCUMENT_COMPLIANCE)[number];

/** Stored under Driver.documentIssues. */
export const driverDocumentIssueSchema = z.object({
  document: z.string(),
  kind: z.enum(DRIVER_DOCUMENT_ISSUE_KINDS),
  /** Who can resolve it: the driver uploads, the admin reviews. */
  actor: z.enum(["DRIVER", "ADMIN"]),
  expiresAt: isoDateTimeSchema.nullable(),
});
export const driverDocumentIssuesSchema = z.array(driverDocumentIssueSchema);
export type DriverDocumentIssue = z.infer<typeof driverDocumentIssueSchema>;

/** What is known about one stored document of the driver. */
export interface DriverDocumentSnapshot {
  name: string;
  /** Current approved version, if any. */
  approved: { expiresAt: Date | string | null } | null;
  /** State of the latest submitted version. */
  latest: "PENDING" | "REJECTED" | "APPROVED" | null;
}

function fileIssue(
  name: string,
  snapshot: DriverDocumentSnapshot | undefined,
  now: Date
): Omit<DriverDocumentIssue, "document"> | null {
  const pending = snapshot?.latest === "PENDING";
  const approved = snapshot?.approved;
  if (!approved) {
    if (pending) return { kind: "PENDING_REVIEW", actor: "ADMIN", expiresAt: null };
    if (snapshot?.latest === "REJECTED")
      return { kind: "REJECTED", actor: "DRIVER", expiresAt: null };
    return { kind: "MISSING", actor: "DRIVER", expiresAt: null };
  }
  const expiresAt = approved.expiresAt == null ? null : new Date(approved.expiresAt).toISOString();
  const { state } = evaluateDocumentValidity(name, approved.expiresAt, now);
  // A submitted renewal moves the next step to the admin.
  const renewalActor = pending ? "ADMIN" : "DRIVER";
  if (state === "EXPIRED") return { kind: "EXPIRED", actor: renewalActor, expiresAt };
  if (state === "MISSING_DATE") return { kind: "DATE_UNVERIFIED", actor: "ADMIN", expiresAt };
  if (state === "ABOUT_TO_EXPIRE" && !pending)
    return { kind: "EXPIRING", actor: "DRIVER", expiresAt };
  return pending ? { kind: "PENDING_REVIEW", actor: "ADMIN", expiresAt } : null;
}

/** Pure: every driver needs every document in `DRIVER_DOCUMENTS`. */
export function evaluateDriverDocumentCompliance(
  snapshots: readonly DriverDocumentSnapshot[],
  now: Date
) {
  const byFile = new Map<string, DriverDocumentSnapshot>();
  for (const snapshot of snapshots) {
    const file = findDriverDocumentFile(snapshot.name);
    if (file) byFile.set(file.name, snapshot);
  }
  const issues: DriverDocumentIssue[] = DRIVER_DOCUMENT_FILES.flatMap((file) => {
    const issue = fileIssue(file.name, byFile.get(file.name), now);
    return issue ? [{ document: file.name, ...issue }] : [];
  });
  const status: DriverDocumentCompliance =
    DRIVER_DOCUMENT_ISSUE_KINDS.find((kind) => issues.some((issue) => issue.kind === kind)) ??
    "COMPLETE";
  return {
    status,
    issues,
    driverActionRequired: issues.some((issue) => issue.actor === "DRIVER"),
    adminActionRequired: issues.some((issue) => issue.actor === "ADMIN"),
  };
}
export type DriverDocumentComplianceResult = ReturnType<typeof evaluateDriverDocumentCompliance>;

export const DRIVER_DOCUMENT_COMPLIANCE_LABELS_FR: Record<DriverDocumentCompliance, string> = {
  EXPIRED: "Document expiré",
  MISSING: "Document manquant",
  REJECTED: "Document refusé",
  EXPIRING: "Expire bientôt",
  PENDING_REVIEW: "À vérifier",
  DATE_UNVERIFIED: "Date à vérifier",
  COMPLETE: "Complet",
};

const issueReasonsFr: Record<DriverDocumentIssueKind, string> = {
  EXPIRED: "expiré",
  MISSING: "à envoyer",
  REJECTED: "refusé, à renvoyer",
  EXPIRING: "expire bientôt",
  PENDING_REVIEW: "en cours de vérification",
  DATE_UNVERIFIED: "date à vérifier",
};

export function driverDocumentIssueLabelFr(issue: Pick<DriverDocumentIssue, "document" | "kind">) {
  const file = findDriverDocumentFile(issue.document);
  return `${file?.label ?? issue.document} : ${issueReasonsFr[issue.kind]}`;
}

/**
 * Driver-facing reminder for the issues the driver can fix, one line per document.
 * Encourages rather than threatens: nothing is blocked by these issues.
 */
export function driverDocumentReminderFr(issues: readonly DriverDocumentIssue[]) {
  const own = issues.filter((issue) => issue.actor === "DRIVER");
  if (own.length === 0) return null;
  const items = new Map<string, string>();
  for (const issue of own) {
    const file = findDriverDocumentFile(issue.document);
    const document = file && findDriverDocument(file.documentId);
    const label = document?.label ?? issue.document;
    if (!items.has(label)) items.set(label, `${label} : ${issueReasonsFr[issue.kind]}`);
  }
  const has = (kind: DriverDocumentIssueKind) => own.some((issue) => issue.kind === kind);
  const count = items.size;
  const copy = has("EXPIRED")
    ? {
        title: "Mettez vos documents à jour",
        body: "Certains de vos documents ont expiré. Envoyez leur nouvelle version pour continuer à recevoir des missions sans interruption.",
      }
    : has("MISSING") || has("REJECTED")
      ? {
          title: "Complétez votre dossier",
          body: `${count} document${count > 1 ? "s" : ""} à envoyer. Un dossier complet vous permet de recevoir des missions sans interruption.`,
        }
      : {
          title: "Vos documents expirent bientôt",
          body: "Envoyez dès maintenant leur nouvelle version pour ne manquer aucune mission.",
        };
  return { ...copy, items: [...items.values()] };
}

/** Days between two reminders about documents the driver still has to send. */
export const DRIVER_DOCUMENT_REMINDER_INTERVAL_DAYS = 7;
/** Expiring documents already follow DOCUMENT_EXPIRY_STEPS; these have no other reminder. */
const REMINDED_KINDS: readonly DriverDocumentIssueKind[] = ["EXPIRED", "MISSING", "REJECTED"];

export function shouldRemindDriverAboutDocuments(
  issues: readonly DriverDocumentIssue[],
  lastSentAt: Date | null,
  now: Date
): boolean {
  const due =
    lastSentAt === null ||
    now.getTime() - lastSentAt.getTime() >= DRIVER_DOCUMENT_REMINDER_INTERVAL_DAYS * 86_400_000;
  return (
    due && issues.some((issue) => issue.actor === "DRIVER" && REMINDED_KINDS.includes(issue.kind))
  );
}
